import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Search, Filter, Edit, Trash2, MapPin } from 'lucide-react'
import useRegionStore from '@/stores/regionStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'

const RegionsPage = () => {
  const {
    regions,
    loading,
    error,
    pagination,
    fetchRegions,
    deleteRegion,
    setFilters,
    clearError
  } = useRegionStore()

  const [searchTerm, setSearchTerm] = useState('')
  const [showEnabledOnly, setShowEnabledOnly] = useState(false)

  useEffect(() => {
    fetchRegions()
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters({ search: searchTerm || undefined, enabled: showEnabledOnly || undefined })
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchTerm, showEnabledOnly])

  const handleDelete = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette région ?')) {
      try {
        await deleteRegion(id)
      } catch (error) {
        console.error('Error deleting region:', error)
      }
    }
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertDescription>{error}</AlertDescription>
        <Button variant="outline" size="sm" onClick={clearError} className="mt-2">
          Fermer
        </Button>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Régions</h1>
          <p className="text-muted-foreground">
            Gérez les régions disponibles dans votre application
          </p>
        </div>
        <Button>
          <Link to="/regions/new" className="flex items-center">
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle région
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Liste des régions</CardTitle>
          <CardDescription>
            {pagination.total} région{pagination.total !== 1 ? 's' : ''} trouvée{pagination.total !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Rechercher une région..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <Button
                variant={showEnabledOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setShowEnabledOnly(!showEnabledOnly)}
              >
                {showEnabledOnly ? 'Toutes' : 'Activées seulement'}
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 w-full bg-gray-200 animate-pulse rounded"></div>
              ))}
            </div>
          ) : regions.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-semibold">Aucune région trouvée</h3>
              <p className="text-gray-500 mt-2">
                {searchTerm || showEnabledOnly
                  ? 'Essayez de modifier vos critères de recherche'
                  : 'Commencez par créer votre première région'}
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Pays ID</TableHead>
                    <TableHead>Taux de taxe</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {regions.map((region) => (
                    <TableRow key={region.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-500" />
                          {region.name}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">{region.code}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {region.country_id || 'Non défini'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {region.tax_rate}%
                      </TableCell>
                      <TableCell>
                        <Badge variant={region.enabled ? "default" : "secondary"}>
                          {region.enabled ? 'Activée' : 'Désactivée'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Link to={`/regions/${region.id}/edit`}>
                            <Button variant="outline" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(region.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-500">
                Page {pagination.page} sur {pagination.pages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === 1}
                  onClick={() => setFilters({ page: pagination.page - 1 })}
                >
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === pagination.pages}
                  onClick={() => setFilters({ page: pagination.page + 1 })}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default RegionsPage
